import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { listStudyTasks } from './studyApi';
import type { StudyNote } from './studyNotesApi';

/** References to existing tasks; saving the note never moves or copies a task. */
export function StudyNoteTaskLinks({
  owner,
  linkedIds,
  savedTasks,
  disabled,
  onChange,
}: {
  owner: string;
  linkedIds: string[];
  savedTasks: NonNullable<StudyNote['linked_tasks']>;
  disabled: boolean;
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const tasks = useQuery({
    queryKey: ['study-tasks', owner],
    queryFn: listStudyTasks,
    enabled: open,
    retry: false,
  });
  const titles = new Map(savedTasks.map((task) => [task.id, task.title]));
  for (const task of tasks.data ?? []) titles.set(task.id, task.title);
  return (
    <section
      className="mt-6 rounded-3xl border border-hairline bg-canvas-pure p-5"
      aria-labelledby="note-task-links"
    >
      <h2 id="note-task-links" className="text-lg font-extrabold text-ink">
        Use these notes for tasks
      </h2>
      <p className="mt-1 text-sm text-slate2">
        Link the same notes to more than one task. Save notes to keep your changes.
      </p>
      {linkedIds.length > 0 && (
        <ul className="mt-3 space-y-2" aria-label="Linked tasks">
          {linkedIds.map((id) => (
            <li
              key={id}
              className="flex items-center justify-between gap-3 rounded-xl bg-wash-sky p-3"
            >
              {titles.has(id) ? (
                <Link
                  className="min-h-11 content-center break-words font-bold text-ink underline"
                  to={`/learn/study?task=${encodeURIComponent(id)}`}
                >
                  {titles.get(id)}
                </Link>
              ) : (
                <span className="text-sm text-slate2">Task unavailable</span>
              )}
              <button
                type="button"
                className="btn-pill-ghost shrink-0"
                disabled={disabled}
                onClick={() => onChange(linkedIds.filter((value) => value !== id))}
                aria-label={`Unlink ${titles.get(id) ?? 'unavailable task'}`}
              >
                Unlink
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="btn-pill-secondary mt-3"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        Choose existing tasks
      </button>
      {open && (
        <div className="mt-3">
          <label className="text-sm font-bold">
            Find a task
            <input
              className="mt-1 block min-h-11 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          {tasks.isPending && (
            <p role="status" className="mt-3 text-slate2">
              Loading tasks…
            </p>
          )}
          {tasks.isError && (
            <p role="alert" className="mt-3 text-ink">
              Tasks could not load.{' '}
              <button type="button" className="btn-pill-ghost" onClick={() => void tasks.refetch()}>
                Try again
              </button>
            </p>
          )}
          {tasks.isSuccess && (
            <>
              <p className="mt-2 text-xs text-slate2">
                Recent tasks. Previously linked tasks stay above.
              </p>
              <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto" aria-label="Available tasks">
                {tasks.data
                  .filter((task) => task.title.toLowerCase().includes(search.toLowerCase()))
                  .map((task) => (
                    <li key={task.id}>
                      <label className="flex min-h-11 items-center gap-3 rounded-xl p-3 text-sm text-ink">
                        <input
                          type="checkbox"
                          checked={linkedIds.includes(task.id)}
                          disabled={
                            disabled || (!linkedIds.includes(task.id) && linkedIds.length >= 50)
                          }
                          onChange={(e) =>
                            onChange(
                              e.target.checked
                                ? [...linkedIds, task.id]
                                : linkedIds.filter((id) => id !== task.id),
                            )
                          }
                        />
                        <span className="min-w-0 break-words">
                          {task.title}
                          <span className="ml-2 text-xs text-slate2">
                            {task.status === 'done' ? 'Completed' : (task.due_on ?? '')}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
              </ul>
              {tasks.data.length === 0 && (
                <p className="mt-3 text-slate2">No tasks yet. Create one on Study Home.</p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
